import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './Accordion.module.scss';
import { setupTransitionInTimeline } from './Accordion.transitions';
import type { AccordionProps, AccordionRef } from './Accordion.data';
import { useScrollTransition } from 'util/react-transition-component';
import AccordionItem from './accordion-item/AccordionItem';

export default function Accordion({
  blok,
  ref,
  ...props
}: AccordionProps & { ref?: Ref<AccordionRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const [openItems, setOpenItems] = useState<boolean[]>(() => blok.items.map(() => false));

  useEffect(() => {
    setOpenItems((prevOpenItems) => {
      const nextOpenItems = blok.items.map((_, index) => prevOpenItems[index] ?? false);
      return nextOpenItems;
    });
  }, [blok.items]);

  useScrollTransition({
    scroll: {
      trigger: elementRef,
      vars: {},
    },
    setupOptions: () => ({
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
    }),
  });

  const onItemToggle = useCallback((itemIndex: number) => {
    setOpenItems((prevOpenItems) => {
      const nextOpenItems = [...prevOpenItems];
      nextOpenItems[itemIndex] = !nextOpenItems[itemIndex];
      return nextOpenItems;
    });
  }, []);

  return (
    <div
      ref={elementRef}
      className={classNames(styles.accordion, props.className)}
      {...sbEditable(blok)}
    >
      {blok.items.map((item, index) => (
        <AccordionItem
          blok={item}
          key={item._uid}
          isOpen={openItems[index] ?? false}
          onClick={() => onItemToggle(index)}
          index={props.numbered ? index : undefined}
        />
      ))}
    </div>
  );
}
